import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { useAuth } from "@/hooks/auth";

/**
 * Reminder visibility for pending email verification.
 *
 * Indicators (in-page banner on /account, sitewide header banner, avatar
 * red dot, Account dropdown badge) should only appear AFTER the user has
 * closed the verify modal at least once. Otherwise we'd nag them while
 * the modal is the active task right after they saved their new email.
 *
 * Persistence: keyed by the actual `pendingEmail` value in localStorage,
 * so a refresh / new tab / new session still remembers that the user has
 * seen the modal for this specific change. If they cancel and start a
 * new change to a different address, the key changes and we start fresh.
 */
const STORAGE_KEY = "pending-email-modal-closed";

interface Ctx {
  /** True iff there's a pendingEmail AND the user has closed the modal at least once. */
  shouldShowReminder: boolean;
  /** The active pending email — convenience so callers don't repeat the auth lookup. */
  pendingEmail: string | null;
  /** Mark the verify modal as having been closed once for the current pendingEmail. */
  markModalClosed: () => void;
}

const PendingEmailReminderContext = createContext<Ctx>({
  shouldShowReminder: false,
  pendingEmail: null,
  markModalClosed: () => { /* default no-op */ },
});

export const PendingEmailReminderProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { myData, isLoading, isLoggedIn } = useAuth();
  const pendingEmail = ((myData as any)?.pendingEmail as string | null | undefined) ?? null;
  const [hasClosed, setHasClosed] = useState(false);

  // Single flag (not keyed by email value) so editing the pending email
  // mid-flow doesn't reset the reminder — the user already saw the modal
  // once, going dark again would feel regressive. Cleared when
  // pendingEmail becomes null FOR A LOGGED-IN USER (verify success or
  // cancel) so a fresh change-flow later starts back at hidden.
  //
  // Two important guards:
  //   - `isLoading` skip — prevents the initial paint (myData not yet
  //     loaded → pendingEmail looks null) from wiping the flag on hard
  //     refresh.
  //   - `!isLoggedIn` skip — logout makes pendingEmail null too, but
  //     wiping the flag there would lose the "user has seen the modal"
  //     state across logout/login, defeating the reminder. So when
  //     logged out, just reset in-memory state without touching storage.
  useEffect(() => {
    if (isLoading) return;
    if (!isLoggedIn) {
      setHasClosed(false);
      return;
    }
    if (!pendingEmail) {
      setHasClosed(false);
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
      return;
    }
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      setHasClosed(stored === "1");
    } catch {
      // localStorage blocked — fail open so the user still gets the reminder.
      setHasClosed(true);
    }
  }, [pendingEmail, isLoading, isLoggedIn]);

  const markModalClosed = useCallback(() => {
    if (!pendingEmail) return;
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setHasClosed(true);
  }, [pendingEmail]);

  return (
    <PendingEmailReminderContext.Provider
      value={{
        shouldShowReminder: !!pendingEmail && hasClosed,
        pendingEmail,
        markModalClosed,
      }}
    >
      {children}
    </PendingEmailReminderContext.Provider>
  );
};

export const usePendingEmailReminder = () => useContext(PendingEmailReminderContext);
